import { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IClientControlOption } from '@/Interfaces/ControlOption.interface';
import CreativeSizeService from '@/Services/Creative/CreativeSize.service';
import DealGroupsService from '@/Services/Deals/DealGroups.service';
import { parseWhitelabelsWithRelations } from '@/helpers/Whitelabels';
import { beeswaxAdvertiserFilter } from '@/helpers/advertisers/advertiserFilters.helpers';
import { parsedAdvertisersMapper, ribeyeAdvertiserMapper } from '@/helpers/advertisers/advertiserMappers.helpers';
import { formatClientsResponseData } from '@/helpers/clients';
import { isRoleAllowedWhitelabelSelect } from '@/helpers/roles';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectMeRole } from './me.selectors';

type AdvertiserMapper = (advertiser: IAdvertiser) => any;
type AdvertiserFilter = ((record: IAdvertiser, i?: number, arr?: IAdvertiser[]) => boolean) | null;

export const selectSharedApi = (state: RootState) => state.sharedApi;

export const selectSharedApiClients = createSelector(
  [
    selectSharedApi,
    (_, advertiserMapper?: AdvertiserMapper, advertiserFilter?: AdvertiserFilter, includeEmpty?: boolean) => ({
      advertiserMapper,
      advertiserFilter,
      includeEmpty,
    }),
  ],
  ({ clients, clientsRequestId }, { advertiserMapper, advertiserFilter, includeEmpty }) => {
    const parsedClients = formatClientsResponseData(
      clients,
      advertiserMapper,
      advertiserFilter,
      includeEmpty,
    ) as IClientControlOption[];

    return { clients: parsedClients, clientsLoading: !!clientsRequestId };
  },
);

export const selectSharedApiWhitelabels = createSelector(
  [
    selectSharedApi,
    (_, advertiserMapper?: AdvertiserMapper, advertiserFilter?: AdvertiserFilter, includeEmpty?: boolean) => ({
      advertiserMapper,
      advertiserFilter,
      includeEmpty,
    }),
  ],
  ({ whitelabels, whitelabelsRequestId }, { advertiserMapper, advertiserFilter, includeEmpty }) => {
    const parsedWhitelabels = parseWhitelabelsWithRelations(
      whitelabels,
      advertiserMapper,
      advertiserFilter,
      includeEmpty,
    ); // mapBeeswaxOnlyAdvertisers mapper is used by default

    return { whitelabels: parsedWhitelabels, whitelabelsLoading: !!whitelabelsRequestId };
  },
);

/** depending on the user role, only one (client or whitelabel) request goes out */
export const selectSharedApiLoading = createSelector(
  [selectSharedApi, selectMeRole],
  ({ clientsRequestId, whitelabelsRequestId }, meRole) => {
    if (isRoleAllowedWhitelabelSelect(meRole)) {
      return !!whitelabelsRequestId;
    }
    return !!clientsRequestId;
  },
);

export const selectSharedApiDealGroups = createSelector([selectSharedApi], ({ dealGroups, dealGroupsRequestId }) => ({
  dealGroups,
  loading: !!dealGroupsRequestId,
}));

export const selectSharedApiDealGroupControlOptions = createSelector(
  [selectSharedApiDealGroups],
  ({ dealGroups, loading }) => ({
    dealGroups: DealGroupsService.dealGroupsToControlOptions(dealGroups),
    loading,
  }),
);

export const selectSharedApiPackages = createSelector([selectSharedApi], ({ packages, packagesRequestId }) => ({
  packages,
  loading: !!packagesRequestId,
}));

export const selectSharedApiCreativeSizes = createSelector(
  selectSharedApi,
  ({ creativeSizes, creativeSizesRequestId }) => ({
    creativeSizes: CreativeSizeService.toGroupControlOptions(creativeSizes),
    loadingCreativeSizes: !!creativeSizesRequestId,
  }),
);

//#region common whitelabel/clients selectors
//#region ribeye advertisers w/ beeswax filter
const ribeyeWithBeeswaxWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, ribeyeAdvertiserMapper, beeswaxAdvertiserFilter);
const ribeyeWithBeeswaxClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, ribeyeAdvertiserMapper, beeswaxAdvertiserFilter);

export const selectRibeyeWithBeeswaxWhitelabels = createSelector(
  [ribeyeWithBeeswaxWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectRibeyeWithBeeswaxClients = createSelector(
  [ribeyeWithBeeswaxClientsSelector],
  ({ clients }) => clients,
);
//#endregion

//#region ribeye advertiser, no filter, all whitelabels/clients
const ribeyeAllWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, ribeyeAdvertiserMapper, null, true);
const ribeyeAllClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, ribeyeAdvertiserMapper, null, true);

export const selectRibeyeAllWhitelabels = createSelector(
  [ribeyeAllWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectRibeyeAllClients = createSelector([ribeyeAllClientsSelector], ({ clients }) => clients);
//#endregion
//#region parsed advertiser, no filter, all whitelabels/clients
const parsedAllWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, parsedAdvertisersMapper, null, true);
const parsedAllClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, parsedAdvertisersMapper, null, true);

export const selectParsedAllWhitelabels = createSelector(
  [parsedAllWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectParsedAllClients = createSelector([parsedAllClientsSelector], ({ clients }) => clients);
//#endregion
//#region parsed advertiser with beeswax filter
const parsedWithBeeswaxWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, parsedAdvertisersMapper, beeswaxAdvertiserFilter);
export const parsedWithBeeswaxClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, parsedAdvertisersMapper, beeswaxAdvertiserFilter);

export const selectParsedWithBeeswaxWhitelabels = createSelector(
  [parsedWithBeeswaxWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectParsedWithBeeswaxClients = createSelector(
  [parsedWithBeeswaxClientsSelector],
  ({ clients }) => clients,
);
//#endregion
//#endregion
